/**
 * st-acc-compact.js — ST 2026-10-17 (Wrike 4585760065, design Iris for Ali, Ali-OK 17.10.)
 *
 * Compact "Beliebtes Zubehör" row (main-product, block product_bundle, section setting st_acc_compact):
 * - desktop pager "1/2" + arrows for the scroll-snap row (hidden while everything fits)
 * - Live card: one state {on, plan}. The card checkbox (name="bundles") IS the state: checked = on, value = the
 *   variant of the selected term. Card, term dropdown, open pop-up and Gesamtpreis (assets/st-buy-total.js via the
 *   theme's bundle:change) render from it. Choosing a term checks Live; opening the dropdown does not.
 * - Live info pop-up (snippets/st-live-info.liquid): native <dialog>, modal from 1024px, bottom sheet below
 *   (swipe down closes). X, backdrop and Esc close; focus goes back to the (i).
 * Variant changes re-render the block, so everything is set up per element in connectedCallback.
 */

if (!customElements.get("st-acc-compact")) {
  customElements.define(
    "st-acc-compact",
    class StAccCompact extends HTMLElement {
      connectedCallback() {
        this.rail = this.querySelector("[data-st-acc-rail]");
        this.nav = this.querySelector("[data-st-acc-nav]");
        this.onNav = this.updateNav.bind(this);
        this.onDocPointer = this.onDocPointer.bind(this);
        this.onDocKey = this.onDocKey.bind(this);
        this.closeTerms = this.closeTerms.bind(this);
        this.onWindowScroll = this.onWindowScroll.bind(this);

        if (this.rail && this.nav) {
          this.nav
            .querySelector("[data-st-acc-prev]")
            .addEventListener("click", () => this.step(-1));
          this.nav
            .querySelector("[data-st-acc-next]")
            .addEventListener("click", () => this.step(1));
          this.rail.addEventListener("scroll", this.onNav, { passive: true });
          window.addEventListener("resize", this.onNav);
          if (window.ResizeObserver) {
            this.resizeObserver = new ResizeObserver(this.onNav);
            this.resizeObserver.observe(this.rail);
          }
          this.updateNav();
        }

        this.setupLive();
      }

      disconnectedCallback() {
        window.removeEventListener("resize", this.onNav);
        if (this.onFitResize)
          window.removeEventListener("resize", this.onFitResize);
        if (this.resizeObserver) this.resizeObserver.disconnect();
        this.closeTerms();
        if (this.pop) this.pop.remove();
      }

      /* ---------- pager ---------- */

      step(direction) {
        const card = this.rail.querySelector(".st-bundle-card");
        const gap = parseFloat(getComputedStyle(this.rail).columnGap) || 0;
        const width = card ? card.getBoundingClientRect().width + gap : 160;
        this.rail.scrollBy({ left: direction * width, behavior: "smooth" });
      }

      updateNav() {
        const max = this.rail.scrollWidth - this.rail.clientWidth - 2;
        this.nav.hidden = max <= 0;
        if (max <= 0) return;
        const left = Math.abs(this.rail.scrollLeft);
        const pages = Math.max(
          1,
          Math.ceil((this.rail.scrollWidth - 4) / this.rail.clientWidth),
        );
        const page =
          left >= max
            ? pages
            : Math.min(
                pages,
                1 + Math.floor((left + 2) / this.rail.clientWidth),
              );
        this.nav.querySelector("[data-st-acc-count]").textContent =
          `${page}/${pages}`;
        this.nav.querySelector("[data-st-acc-prev]").disabled = left <= 2;
        this.nav.querySelector("[data-st-acc-next]").disabled = left >= max;
        if (this.pop && !this.pop.hidden) this.closeTerms();
      }

      /* ---------- Live card ---------- */

      setupLive() {
        this.card = this.querySelector("[data-st-live]");
        if (!this.card) return;
        try {
          this.plans = JSON.parse(
            this.card.querySelector("[data-st-live-plans]").textContent,
          );
        } catch (_error) {
          this.plans = [];
        }
        if (this.plans.length === 0) return;

        this.uid = this.card.dataset.stLive;
        this.check = this.card.querySelector("[data-st-live-check]");
        this.dd = this.card.querySelector("[data-st-live-dd]");
        this.infoButton = this.card.querySelector("[data-st-live-open]");
        this.dialog = document.getElementById(`StLiveInfo-${this.uid}`);

        // the dropdown and the (i) sit inside the card's <label>: they never toggle the checkbox
        this.dd.addEventListener("click", (event) => {
          event.preventDefault();
          event.stopPropagation();
          if (this.pop && !this.pop.hidden) {
            this.closeTerms();
          } else {
            this.openTerms();
          }
        });
        this.infoButton.addEventListener("click", (event) => {
          event.preventDefault();
          event.stopPropagation();
          this.closeTerms();
          this.openDialog();
        });
        this.check.addEventListener("change", () => this.render());

        if (this.dialog) this.setupDialog();
        this.render();
      }

      get planIndex() {
        const index = this.plans.findIndex(
          (plan) => String(plan.id) === this.check.value,
        );
        return index < 0 ? 0 : index;
      }

      get on() {
        return this.check.checked;
      }

      // One state change: term (optional) and on/off, then notify the theme (bundle:change → Gesamtpreis).
      setState(index, on) {
        const plan = this.plans[index];
        if (!plan || this.check.disabled) return;
        if (on && !plan.available) return;
        this.check.value = plan.id;
        this.check.setAttribute("data-price", plan.price);
        this.check.setAttribute("data-compare-at", plan.compare || 0);
        this.check.setAttribute("data-st-name", plan.name);
        this.check.checked = on;
        this.check.dispatchEvent(new Event("change", { bubbles: true }));
      }

      render() {
        const plan = this.plans[this.planIndex];
        this.card.querySelector("[data-st-live-label]").textContent =
          plan.label;
        this.card.querySelector("[data-st-live-price]").textContent =
          plan.priceText;
        const compare = this.card.querySelector("[data-st-live-compare]");
        compare.hidden = !plan.compare;
        compare.textContent = plan.compare ? plan.compareText : "";
        if (this.pop) this.renderTerms();
        if (this.dialog) this.renderDialog();
      }

      /* ---------- term dropdown (popover in <body>, position: fixed) ---------- */

      openTerms() {
        if (!this.pop) {
          this.pop = document.createElement("div");
          this.pop.className = "st-acc-pop";
          this.pop.id = this.dd.getAttribute("aria-controls");
          this.pop.setAttribute("role", "listbox");
          this.pop.setAttribute("aria-label", this.dd.dataset.listLabel || "");
          this.pop.hidden = true;
          this.pop.addEventListener("click", (event) => {
            const option = event.target.closest("[data-index]");
            if (!option || option.getAttribute("aria-disabled") === "true")
              return;
            this.setState(parseInt(option.dataset.index, 10), true);
            this.closeTerms();
            this.dd.focus({ preventScroll: true });
          });
          this.pop.addEventListener("keydown", (event) =>
            this.onTermsKey(event),
          );
          this.renderTerms();
        }
        document.body.appendChild(this.pop);
        this.pop.hidden = false;
        this.dd.setAttribute("aria-expanded", "true");
        this.placeTerms();
        this.pop
          .querySelector('[aria-selected="true"]')
          ?.focus({ preventScroll: true });
        document.addEventListener("pointerdown", this.onDocPointer, true);
        document.addEventListener("keydown", this.onDocKey);
        this.openedAt = performance.now();
        window.addEventListener("scroll", this.onWindowScroll, {
          passive: true,
        });
        window.addEventListener("resize", this.closeTerms);
      }

      closeTerms() {
        if (!this.pop || this.pop.hidden) return;
        this.pop.hidden = true;
        this.dd.setAttribute("aria-expanded", "false");
        document.removeEventListener("pointerdown", this.onDocPointer, true);
        document.removeEventListener("keydown", this.onDocKey);
        window.removeEventListener("scroll", this.onWindowScroll);
        window.removeEventListener("resize", this.closeTerms);
      }

      renderTerms() {
        const current = this.planIndex;
        this.pop.innerHTML = "";
        this.plans.forEach((plan, index) => {
          const option = document.createElement("button");
          option.type = "button";
          option.className = "st-acc-pop__option";
          option.setAttribute("role", "option");
          option.dataset.index = index;
          option.setAttribute("aria-selected", String(index === current));
          if (!plan.available) option.setAttribute("aria-disabled", "true");
          option.innerHTML =
            '<span class="st-acc-pop__check" aria-hidden="true"></span><span class="st-acc-pop__label"></span><span class="st-acc-pop__price"></span>';
          option.querySelector(".st-acc-pop__label").textContent = plan.label;
          option.querySelector(".st-acc-pop__price").textContent =
            plan.priceText;
          this.pop.appendChild(option);
        });
      }

      // Page scroll closes the popover. Some PDPs shift the page a few pixels right after the tap (sticky bars,
      // lazy content); during the first 350ms the popover only follows the button.
      onWindowScroll() {
        if (performance.now() - this.openedAt < 350) {
          this.placeTerms();
        } else {
          this.closeTerms();
        }
      }

      placeTerms() {
        const rect = this.dd.getBoundingClientRect();
        const height = this.pop.offsetHeight;
        const width = this.pop.offsetWidth;
        let top = rect.bottom + 4;
        if (top + height > window.innerHeight - 8)
          top = Math.max(8, rect.top - 4 - height);
        const left = Math.max(
          8,
          Math.min(rect.left, window.innerWidth - width - 8),
        );
        this.pop.style.top = `${top}px`;
        this.pop.style.left = `${left}px`;
      }

      onTermsKey(event) {
        const options = Array.from(this.pop.querySelectorAll("[role=option]"));
        const index = options.indexOf(document.activeElement);
        let next = null;
        if (event.key === "ArrowDown")
          next = options[Math.min(options.length - 1, index + 1)];
        if (event.key === "ArrowUp") next = options[Math.max(0, index - 1)];
        if (event.key === "Home") next = options[0];
        if (event.key === "End") next = options[options.length - 1];
        if (next) {
          event.preventDefault();
          next.focus({ preventScroll: true });
        }
        if (event.key === "Tab") this.closeTerms();
      }

      onDocPointer(event) {
        if (this.pop.contains(event.target) || this.dd.contains(event.target))
          return;
        this.closeTerms();
      }

      onDocKey(event) {
        if (event.key !== "Escape") return;
        event.preventDefault();
        this.closeTerms();
        this.dd.focus({ preventScroll: true });
      }

      /* ---------- Live info pop-up ---------- */

      setupDialog() {
        const dialog = this.dialog;
        if (dialog.dataset.stLvReady) {
          // block re-rendered: the dialog is new as well, but guard against double binding
          return;
        }
        dialog.dataset.stLvReady = "true";
        // Round 3 review only (Ali 18.10.): ?st_lv=a shows draft A (full-size comparison image) instead of B.
        // Remove together with the unused draft once Ali has decided.
        if (new URLSearchParams(window.location.search).get("st_lv") === "a")
          dialog.classList.remove("st-lv--b");
        this.plansEl = dialog.querySelector("[data-st-lv-plans]");
        this.addButton = dialog.querySelector("[data-st-lv-add]");
        // desktop: remove link under the button; sheet: remove link in the price row (round 2)
        this.removeButtons = dialog.querySelectorAll("[data-st-lv-remove]");
        const name = `st-live-plan-${this.uid}`;
        const noForm = `StLiveInfo-${this.uid}-noform`;

        this.plans.forEach((plan, index) => {
          const label = document.createElement("label");
          label.className = "st-lv__plan";
          label.innerHTML =
            '<input type="radio"><span class="st-lv__plan-t"><span></span><span class="st-lv__plan-save"></span></span><span class="st-lv__plan-p"></span><span class="st-lv__plan-o"><s></s><span></span></span>';
          const input = label.querySelector("input");
          input.name = name;
          input.value = index;
          // not part of the product form
          input.setAttribute("form", noForm);
          input.disabled = !plan.available;
          label.querySelector(".st-lv__plan-t > span").textContent =
            plan.monthsLabel;
          const save = label.querySelector(".st-lv__plan-save");
          save.textContent = plan.percent
            ? (dialog.dataset.tSaving || "").replace("[percent]", plan.percent)
            : "";
          save.hidden = !plan.percent;
          label.querySelector(".st-lv__plan-p").textContent = plan.priceText;
          const strike = label.querySelector(".st-lv__plan-o s");
          strike.textContent = plan.compare ? plan.compareText : "";
          strike.hidden = !plan.compare;
          label.querySelector(".st-lv__plan-o span").textContent =
            plan.monthlyText
              ? (dialog.dataset.tMonthly || "").replace(
                  "[price]",
                  plan.monthlyText,
                )
              : "";
          this.plansEl.appendChild(label);
        });

        this.plansEl.addEventListener("change", (event) => {
          if (event.target.name !== name) return;
          this.setState(parseInt(event.target.value, 10), true);
        });
        this.addButton.addEventListener("click", () => {
          if (!this.on) this.setState(this.planIndex, true);
          dialog.close();
        });
        this.removeButtons.forEach((button) =>
          button.addEventListener("click", () => {
            this.setState(this.planIndex, false);
            this.addButton.focus({ preventScroll: true });
          }),
        );

        dialog
          .querySelector("[data-st-lv-close]")
          .addEventListener("click", () => dialog.close());
        // backdrop: the dialog box has no padding, so a click on the dialog itself is outside the content
        dialog.addEventListener("click", (event) => {
          if (event.target === dialog) dialog.close();
        });
        // a theme-wide keydown handler calls preventDefault() on Escape, which cancels the native dialog close
        dialog.addEventListener("keydown", (event) => {
          if (event.key !== "Escape") return;
          event.stopPropagation();
          dialog.close();
        });
        dialog.addEventListener("close", () => {
          document.documentElement.classList.remove("st-lv-lock");
          this.inner().style.transform = "";
          if (this.infoButton && this.infoButton.isConnected)
            this.infoButton.focus({ preventScroll: true });
        });

        const range = dialog.querySelector("[data-st-lv-range]");
        if (range) {
          const split = range.closest(".st-lv__split");
          const move = () =>
            split.style.setProperty("--st-lv-pos", `${range.value}%`);
          // device modal (Wrike 4585829905): approved design starts at 30 % on desktop; the sheet keeps 50 %
          const startDesktop = split.dataset.stLvStartDesktop;
          if (
            startDesktop &&
            window.matchMedia("(min-width: 1024px)").matches
          ) {
            range.value = startDesktop;
            move();
          }
          range.addEventListener("input", move);
          range.addEventListener("change", move);
          // device sheet (round 3): if the Free Mode values do not fit left of the slider start with 12px room
          // (long labels fr/es/it/nl, 320px), show them as rows; round 6: same for the console values (Halterung)
          if (split.hasAttribute("data-st-lv-fit")) {
            const free = split.querySelector(
              ".st-lv__scr--free, .st-lv__scr--console",
            );
            const box = split.querySelector(".st-lv__split-box");
            const start = parseFloat(range.getAttribute("value"));
            this.fitFree = () => {
              free.classList.remove("st-lv__scr--rows");
              if (!window.matchMedia("(max-width: 1023px)").matches) return;
              const rect = box.getBoundingClientRect();
              if (!rect.width) return;
              const limit = rect.left + (rect.width * start) / 100 - 12;
              const right = Math.max(
                ...Array.from(
                  free.querySelectorAll(
                    ".st-lv__free-k, .st-lv__free-go, .st-lv__k3",
                  ),
                  (el) => el.getBoundingClientRect().right,
                ),
              );
              if (right > limit) free.classList.add("st-lv__scr--rows");
            };
            this.onFitResize = () => {
              if (dialog.open) this.fitFree();
            };
            window.addEventListener("resize", this.onFitResize);
          }
        }

        this.setupSwipe();
      }

      inner() {
        return this.dialog.querySelector(".st-lv__inner");
      }

      // Bottom sheet (below 1024px): drag the head down; more than 80px closes.
      setupSwipe() {
        const grip = this.dialog.querySelector("[data-st-lv-grip]");
        const inner = this.inner();
        let startY = null;
        let delta = 0;
        const sheet = () => window.matchMedia("(max-width: 1023px)").matches;
        grip.addEventListener(
          "touchstart",
          (event) => {
            if (!sheet()) return;
            startY = event.touches[0].clientY;
            delta = 0;
            inner.style.transition = "none";
          },
          { passive: true },
        );
        grip.addEventListener(
          "touchmove",
          (event) => {
            if (startY === null) return;
            delta = Math.max(0, event.touches[0].clientY - startY);
            inner.style.transform = `translateY(${delta}px)`;
          },
          { passive: true },
        );
        const end = () => {
          if (startY === null) return;
          startY = null;
          inner.style.transition = "";
          if (delta > 80) {
            this.dialog.close();
          } else {
            inner.style.transform = "";
          }
        };
        grip.addEventListener("touchend", end);
        grip.addEventListener("touchcancel", end);
      }

      openDialog() {
        if (!this.dialog || this.dialog.open) return;
        this.renderDialog();
        document.documentElement.classList.add("st-lv-lock");
        this.dialog.showModal();
        if (this.fitFree) this.fitFree();
        const body = this.dialog.querySelector("[data-st-lv-body]");
        if (body) body.scrollTop = 0;
        this.dialog
          .querySelector("[data-st-lv-close]")
          .focus({ preventScroll: true });
      }

      renderDialog() {
        if (!this.plansEl) return;
        const index = this.planIndex;
        const plan = this.plans[index];
        this.plansEl.querySelectorAll("input").forEach((input) => {
          input.checked = parseInt(input.value, 10) === index;
        });
        const data = this.dialog.dataset;
        const template = this.on ? data.tAdded : data.tAdd;
        this.addButton.querySelector("[data-st-lv-add-d]").textContent = (
          template || ""
        ).replace("[price]", plan.priceText);
        this.addButton.querySelector("[data-st-lv-add-m]").textContent =
          (this.on ? data.tAddedM : data.tAddM) || "";
        this.addButton.classList.toggle("is-done", this.on);
        this.removeButtons.forEach((button) => {
          button.hidden = !this.on;
        });

        // sheet footer (round 3): "price · term", then "strike · monthly" or, while Live is on, "monthly · Entfernen".
        // The monthly price is cut, not rounded (Liquid divides the cents, Ali 18.10.): 7,49 / 6,24 / 5,27 €.
        this.dialog.querySelector("[data-st-lv-sum-price]").textContent =
          plan.priceText;
        this.dialog.querySelector("[data-st-lv-sum-term]").textContent =
          plan.monthsLabel ? ` · ${plan.monthsLabel}` : "";
        const strike = this.dialog.querySelector("[data-st-lv-sum-compare]");
        strike.textContent = plan.compare ? plan.compareText : "";
        strike.hidden = this.on || !plan.compare;
        const monthly = plan.monthlyText
          ? (data.tMonthly || "").replace("[price]", plan.monthlyText)
          : "";
        let line = monthly;
        if (!this.on && !strike.hidden && monthly) line = ` · ${monthly}`;
        if (this.on && monthly) line = `${monthly} · `;
        this.dialog.querySelector("[data-st-lv-sum-monthly]").textContent =
          line;
      }
    },
  );
}
